/* oxlint-disable require-await */
import type { LocaleCode } from '@repo/shared-config/app/constants/locales';
import { queryOptions } from '@tanstack/react-query';
import { registerBaseURL } from '#constants/hosts';
import { client, type ResponseError } from '#queries/utils';
import type {
  ApiRegistrationState,
  ApiRegistrationStateActive,
  ApiRegistrationStateV2Response,
} from './types';

export const REGISTRATION_STATES_URL = `${registerBaseURL}/v1/registration_states`;
export const REGISTRATION_STATES_V2_URL = `${registerBaseURL}/v2/registration_states`;

export async function getRegistrationState(id: string): Promise<ApiRegistrationStateActive> {
  const json = await client<{ registration_state: ApiRegistrationStateActive }>(
    `${REGISTRATION_STATES_URL}/${id}`
  );

  return json.registration_state;
}

export async function getRegistrationStateV2<TSchemaItem = unknown>(
  id: string,
  locale: LocaleCode
): Promise<ApiRegistrationStateV2Response<TSchemaItem>> {
  return client<ApiRegistrationStateV2Response<TSchemaItem>>(
    `${REGISTRATION_STATES_V2_URL}/${id}`,
    {
      headers: {
        'X-Qonto-Locale': locale,
      },
    }
  );
}

export async function createRegistrationStateV2(
  legalCountry: string
): Promise<ApiRegistrationStateV2Response> {
  return client<ApiRegistrationStateV2Response>(REGISTRATION_STATES_V2_URL, {
    method: 'POST',
    body: JSON.stringify({
      registration_state: { organization: { legal_country: legalCountry } },
    }),
  });
}

export const registrationStateQueries = {
  all: ['registration-states'] as const,
  lists: () => [...registrationStateQueries.all, 'list'] as const,
  items: () => [...registrationStateQueries.all, 'items'] as const,
  item: (id: string) =>
    queryOptions<
      ApiRegistrationState,
      ResponseError,
      ApiRegistrationState,
      readonly ['registration-states', 'items', string]
    >({
      queryKey: [...registrationStateQueries.items(), id] as const,
      queryFn: async () => {
        return getRegistrationState(id);
      },
    }),
};

export const registrationStateV2Queries = {
  all: ['registration-states-v2'] as const,
  items: () => [...registrationStateV2Queries.all, 'items'] as const,
  item: <TSchemaItem = unknown>(id: string, locale: LocaleCode) =>
    queryOptions<
      ApiRegistrationStateV2Response<TSchemaItem>,
      ResponseError,
      ApiRegistrationStateV2Response<TSchemaItem>,
      readonly ['registration-states-v2', 'items', string, LocaleCode]
    >({
      queryKey: [...registrationStateV2Queries.items(), id, locale] as const,
      queryFn: async () => {
        return getRegistrationStateV2<TSchemaItem>(id, locale);
      },
    }),
};
